// ---------------------------------------------------------------------------
// YouAreYou (uru) — Toast Container
//
// Renders ephemeral toast notifications in the top-right corner (desktop)
// or top of screen (mobile). Reads from NotificationContext.
//
// Toasts auto-dismiss after 4 seconds (managed by NotificationContext).
// Clicking a toast with an actionUrl navigates there and dismisses it.
// ---------------------------------------------------------------------------

import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/hooks/useNotifications";
import Icon from "@/components/ui/Icon";

export default function ToastContainer() {
  const { toasts, dismissToast } = useNotifications();
  const navigate = useNavigate();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[200] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => {
        const iconName =
          toast.kind === "success"
            ? "check_circle"
            : toast.kind === "warning"
              ? "warning"
              : "info";

        const colorClass =
          toast.kind === "success"
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : toast.kind === "warning"
              ? "border-amber-200 bg-amber-50 text-amber-800"
              : "border-primary-200 bg-primary-50 text-primary-800";

        const iconColorClass =
          toast.kind === "success"
            ? "text-emerald-500"
            : toast.kind === "warning"
              ? "text-amber-500"
              : "text-primary-500";

        return (
          <div
            key={toast.id}
            role="alert"
            className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-md transition-all ${colorClass}`}
            style={{ animation: "slideInRight 0.2s ease-out" }}
          >
            <Icon
              name={iconName}
              filled
              className={`mt-0.5 shrink-0 text-lg ${iconColorClass}`}
            />
            <p
              className={`flex-1 text-sm font-medium ${
                toast.actionUrl ? "cursor-pointer hover:underline" : ""
              }`}
              onClick={() => {
                if (toast.actionUrl) {
                  dismissToast(toast.id);
                  if (toast.actionUrl.startsWith("/")) {
                    navigate(toast.actionUrl);
                  } else {
                    window.open(toast.actionUrl, "_blank");
                  }
                }
              }}
            >
              {toast.message}
            </p>
            <button
              onClick={() => dismissToast(toast.id)}
              className="shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100"
              aria-label="Dismiss"
            >
              <Icon name="close" className="text-base" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
